import { supabase } from "@/lib/supabase-client";

// Comment table names
export type CommentTable = "clients" | "contacts" | "projects" | "shifts" | "users" | "investigations" | "incidents";

// Meta data structure for comments
export interface CommentMetaData {
  likes?: string[]; // user IDs who liked
  dislikes?: string[]; // user IDs who disliked
  deleted?: boolean; // soft delete flag
  edited?: boolean; // flag that comment was edited
  history?: {
    // edit history (previous versions)
    message: string;
    edited_at: string;
  }[];
}

// Base comment structure shared across all tables
export interface BaseComment {
  id: string;
  author_id: string;
  author_first_name: string;
  author_preferred_name?: string;
  author_last_name: string;
  message: string;
  created_at: string;
  meta_data?: CommentMetaData;
}

// Specific comment types with their foreign keys
export interface ClientComment extends BaseComment {
  client_id: string;
}

export interface ContactComment extends BaseComment {
  contact_id: string;
}

export interface ProjectComment extends BaseComment {
  client_id: string;
  project_id: string;
}

export interface ShiftComment extends BaseComment {
  client_id: string;
  project_id: string;
  shift_id: string;
}

export interface UserComment extends BaseComment {
  user_id: string;
}

export interface InvestigationComment extends BaseComment {
  investigation_id: string;
}

export interface IncidentComment extends BaseComment {
  incident_id: string;
}

// Union type for all comment types
export type Comment =
  | ClientComment
  | ContactComment
  | ProjectComment
  | ShiftComment
  | UserComment
  | InvestigationComment
  | IncidentComment;

// Input types for creating comments (without id, created_at, author_id - all handled by DB)
export type CommentInput<T extends CommentTable> = T extends "clients"
  ? Omit<ClientComment, "id" | "created_at" | "author_id">
  : T extends "contacts"
    ? Omit<ContactComment, "id" | "created_at" | "author_id">
    : T extends "projects"
      ? Omit<ProjectComment, "id" | "created_at" | "author_id">
      : T extends "shifts"
        ? Omit<ShiftComment, "id" | "created_at" | "author_id">
        : T extends "users"
          ? Omit<UserComment, "id" | "created_at" | "author_id">
          : T extends "investigations"
            ? Omit<InvestigationComment, "id" | "created_at" | "author_id">
            : T extends "incidents"
              ? Omit<IncidentComment, "id" | "created_at" | "author_id">
              : never;

// Helper to get the FK column name for each table
function getFKColumn(table: CommentTable): string {
  const fkMap: Record<CommentTable, string> = {
    clients: "client_id",
    contacts: "contact_id",
    projects: "project_id",
    shifts: "shift_id",
    users: "user_id",
    investigations: "investigation_id",
    incidents: "incident_id",
  };
  return fkMap[table];
}

export const commentService = {
  /**
   * Get all comments for a specific entity
   */
  async getComments(table: CommentTable, entityId: string): Promise<Comment[]> {
    const fkColumn = getFKColumn(table);

    const { data, error } = await supabase
      .schema("comments")
      .from(table)
      .select("*")
      .eq(fkColumn, entityId)
      .order("created_at", { ascending: false });

    if (error) {
      throw new Error(`Failed to fetch comments: ${error.message}`);
    }

    const userIds = [...new Set(data.map((item) => item.author_id).filter(Boolean))] as string[];

    let userMap = new Map<
      string,
      { author_first_name: string; author_preferred_name: string; author_last_name: string }
    >();
    if (userIds.length > 0) {
      const { data: users } = await supabase
        .from("users")
        .select(
          "author_id:id, author_first_name:first_name, author_preferred_name:preferred_name, author_last_name: last_name",
        )
        .in("id", userIds);

      if (users) {
        userMap = new Map(users.map((u) => [u.author_id, u]));
      }
    }

    return data.map((comment) => ({
      ...comment,
      ...userMap.get(comment.author_id),
    }));
  },

  /**
   * Add a new comment
   */
  async addComment<T extends CommentTable>(table: T, comment: CommentInput<T>): Promise<Comment> {
    const { data, error } = await supabase.schema("comments").from(table).insert([comment]).select().single();

    if (error) {
      throw new Error(`Failed to create comment: ${error.message}`);
    }

    return data as Comment;
  },

  /**
   * Update an existing comment
   */
  async updateComment(
    table: CommentTable,
    commentId: string,
    patch: Partial<Pick<BaseComment, "message" | "meta_data">>,
  ): Promise<Comment> {
    const { data, error } = await supabase
      .schema("comments")
      .from(table)
      .update(patch)
      .eq("id", commentId)
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to update comment: ${error.message}`);
    }

    return data as Comment;
  },

  /**
   * Delete a comment
   */
  async deleteComment(table: CommentTable, commentId: string): Promise<boolean> {
    const { error } = await supabase.schema("comments").from(table).delete().eq("id", commentId);

    if (error) {
      throw new Error(`Failed to delete comment: ${error.message}`);
    }

    return true;
  },

  /**
   * Delete all comments for a specific entity
   */
  async deleteCommentsByEntity(table: CommentTable, entityId: string): Promise<boolean> {
    const fkColumn = getFKColumn(table);

    const { error } = await supabase.schema("comments").from(table).delete().eq(fkColumn, entityId);

    if (error) {
      throw new Error(`Failed to delete comments: ${error.message}`);
    }

    return true;
  },
};
